import { Content } from "@/ui";
import { employer_page_helper } from "@/helpers/employer_page_helper";
import { getRoute } from "@/getters/getRoute";
import { createTitle } from "@/functions/createTitle";
import { trimText } from "@/functions/trimText";
import * as additionalComponents from "./__components";
import { jobs_helper } from "@/helpers/jobs_helper";
import { employer_helper } from "@/helpers/employer_helper";
import { getNestedRoutes } from "@/functions/getNestedRoutes";
import { convertColorName } from "@/functions/convertColorName";
import sn from "@/snippets";
import { DynamicPageSeo } from "@/ui/DynamicPageSeo";
import { ContentBlocks } from "@/ui/ContentBlocks";

/**
 * @param content
 * @param prefix
 * @param title
 * @param seo
 * @param canonical
 * @param data
 * @param isBuilder
 * @returns {JSX.Element}
 * @constructor
 */

export default function EmployerPage({ content, seo, title, canonical, data, prefix, isBuilder = false }) {
  return (
    <>
      {isBuilder ? (
        <>
          <DynamicPageSeo
            {...seo}
            title={seo?.title.length > 0 ? seo.title : title}
            canonical={seo?.canonical.length > 0 ? seo.canonical : canonical}
          />
          <ContentBlocks data={data} prefix={prefix} />
        </>
      ) : (
        <>
          <Content items={content} additionalComponents={additionalComponents} />
        </>
      )}
    </>
  );
}

export async function getStaticProps({ params: { url_slugs } }) {
  const pages = getNestedRoutes({ url_slugs, routePrefix: `/great-places-to-work/` });
  const [rootPage] = pages;
  const [currentPage, prevPage] = [...pages].reverse();
  const page = employer_page_helper.find(currentPage.url_slug);
  const root = employer_page_helper.find(rootPage.url_slug);
  const employerName = root.employer ?? null;
  const employer = employerName && employer_helper.find(employerName, "name");
  const parent = employer_page_helper.find(page.parent.id, "id");
  const children = employer_page_helper.fetch({ filter: (i) => i.parent.id === page.id });
  const siblings = employer_page_helper.fetch({ exclude: [page.id], filter: (i) => i.parent.id === page.parent.id });
  const textColor = convertColorName(page["header_text_color"]?.toLowerCase() ?? "tertiary");
  const backgroundColor = convertColorName(page["header_background_color"]?.toLowerCase() ?? "light");

  return {
    props: {
      meta: {
        title: createTitle(page.title),
        canonical:
          "https://www.bluearrow.co.uk" +
          getRoute("employerPage", { url_slug: pages.map((page) => page.url_slug).join("/") }),
      },
      content: [
        {
          component: "BreadcrumbNavigation",
          props: {
            items: [
              { label: "Find a Job", href: getRoute("jobs") },
              { label: "Great Places To Work", href: getRoute("employers") },
              ...pages,
            ],
          },
        },
        {
          component: "Header",
          props: {
            className: `text-${textColor} bg-${backgroundColor}`,
            title: page.title,
            description: page.description ?? null,
            img: page.cover_image ?? null,
            variant: page.header_image_position ?? null,
            back: prevPage
              ? {
                  path: `page.${prevPage.url_slug}.component.Header.back`,
                  placeholder: `Back to ${prevPage.label}`,
                  href: prevPage.href,
                }
              : {
                  path: `page.home.component.Header.back`,
                  placeholder: `Back to Home`,
                  href: getRoute("home"),
                },
          },
        },
        ...(page.parent?.id === null
          ? [{ component: "RichText", props: { className: "bg-primary text-white py-5", body: page.body ?? null } }]
          : [
              {
                component: "EmployerPageArticleContent",
                props: {
                  body: page.body ?? null,
                  title: page.title ?? null,
                  url_slug: page.url_slug ?? null,
                  url_slugs,
                  children,
                  siblings,
                  parent,
                },
              },
            ]),
        sn.collapsibleSection({ page }),
        ...sn.narrativePanels({ page }),

        { component: "Divider" },
        {
          component: "LatestJobs",
          props: {
            title: {
              path: `page.${url_slugs.join(".")}.component.LatestJobs.title`,
              placeholder: `Latest ${employer && employer?.name} Jobs`,
            },
            items: jobs_helper.fetch({
              limit: 20,
              employer: page.employer,
              filter: (i) =>
                "related_jobs_keyword" in page && page["related_jobs_keyword"].length > 0
                  ? i.title.toLowerCase().includes(page["related_jobs_keyword"].toLowerCase().trim())
                  : true,
            }),
            visibleCount: 4,
          },
        },
        ...(page.parent?.id === null
          ? [
              ...(children.length > 0
                ? [
                    { component: "Divider" },
                    {
                      component: "PromoSection",
                      props: {
                        title: {
                          path: `page.${url_slugs.join(".")}.component.PromoSection.title`,
                          placeholder: `In this section`,
                        },
                        items: children.map((i) => ({
                          title: i.title ?? null,
                          img: i.card_image ?? null,
                          description: trimText(i.body),
                          href: getRoute("employers", { url_slugs: [...url_slugs, i.url_slug] }),
                        })),
                      },
                    },
                  ]
                : []),
            ]
          : !children
          ? [
              ...(siblings.length > 0
                ? [
                    { component: "Divider" },
                    {
                      component: "PromoSection",
                      props: {
                        title: {
                          path: `page.${url_slugs.join(".")}.component.PromoSection.title`,
                          placeholder: `Also in this section`,
                        },
                        items: siblings.map((i) => ({
                          title: i.title ?? null,
                          img: i.card_image ?? null,
                          description: trimText(i.body),
                          href: getRoute("employerPage", {
                            url_slugs: [...url_slugs.filter((i) => i !== page.url_slug), i.url_slug],
                          }),
                        })),
                      },
                    },
                  ]
                : []),
            ]
          : []),
      ],
    },
  };
}

export async function getStaticPaths() {
  const paths = employer_page_helper.toNestedPaths();
  const employerPages = employer_page_helper.fetch();

  const skipped = paths.filter((path) => {
    const urlSlugs = path.params.url_slugs;
    const currentPageSlug = urlSlugs[urlSlugs.length - 1];
    const foundPage = employerPages.find((page) => page.url_slug === currentPageSlug);
    return foundPage?.page_builder !== true;
  });

  return {
    paths: skipped,
    fallback: false,
  };
}

// export async function getStaticPaths() {
//   return {
//     paths: employer_page_helper.toNestedPaths(),
//     fallback: false,
//   };
// }
